import { signUp, signIn, signOut, getCurrentUser, confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';

export const authService = {
  async signup(userData) {
    try {
      const { firstName, lastName, email, password } = userData;
      const fullName = `${firstName} ${lastName}`.trim();
      
      const { isSignUpComplete, userId, nextStep } = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            name: fullName,
            given_name: firstName,
            family_name: lastName,
          },
        },
      });

      return {
        isSignUpComplete,
        userId,
        nextStep,
        user: {
          email,
          firstName,
          lastName,
          name: fullName
        }
      };
    } catch (error) {
      console.error('Signup error:', error);
      throw new Error(error.message || 'Signup failed');
    }
  },

  async confirmSignup(email, confirmationCode) {
    try {
      const { isSignUpComplete, nextStep } = await confirmSignUp({
        username: email,
        confirmationCode
      });

      return { isSignUpComplete, nextStep };
    } catch (error) {
      console.error('Confirmation error:', error);
      throw new Error(error.message || 'Confirmation failed');
    }
  },

  async resendConfirmationCode(email) {
    try {
      await resendSignUpCode({
        username: email
      });
      return { success: true };
    } catch (error) {
      console.error('Resend code error:', error);
      throw new Error(error.message || 'Failed to resend code');
    }
  },

  async login(credentials) {
    try {
      const { email, password } = credentials;
      
      const { isSignedIn, nextStep } = await signIn({
        username: email,
        password
      });

      if (isSignedIn) {
        // Small delay to ensure Cognito session is fully established
        await new Promise(resolve => setTimeout(resolve, 100));
        const user = await this.getCurrentUser();
        return {
          isSignedIn,
          user,
          nextStep
        };
      }

      return { isSignedIn, nextStep };
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle specific Cognito error cases
      if (error.name === 'UserAlreadyAuthenticatedException') {
        // User is already signed in, get current user
        const user = await this.getCurrentUser();
        return {
          isSignedIn: true,
          user,
          nextStep: null
        };
      }
      
      throw new Error(error.message || 'Login failed');
    }
  },

  async logout() {
    try {
      await signOut();
    } catch (error) {
      console.error('Logout error:', error);
      throw new Error(error.message || 'Logout failed');
    }
  },

  async getCurrentUser() {
    try {
      const { username, userId, signInDetails } = await getCurrentUser();
      
      // Get user attributes
      const userAttributes = await this.getUserAttributes();
      
      return {
        username,
        userId,
        email: userAttributes.email,
        name: userAttributes.name,
        firstName: userAttributes.given_name,
        lastName: userAttributes.family_name,
        emailVerified: userAttributes.email_verified === 'true',
        signInDetails
      };
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  },

  async getUserAttributes() {
    try {
      const { fetchUserAttributes } = await import('aws-amplify/auth');
      return await fetchUserAttributes();
    } catch (error) {
      console.error('Get user attributes error:', error);
      return {};
    }
  },

  getToken() {
    // For Cognito, tokens are managed automatically by Amplify
    // You can get them if needed for API calls
    return this.getCognitoTokens();
  },

  async getCognitoTokens() {
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      return {
        accessToken: session.tokens?.accessToken?.toString(),
        idToken: session.tokens?.idToken?.toString(),
        refreshToken: session.tokens?.refreshToken?.toString()
      };
    } catch (error) {
      console.error('Get tokens error:', error);
      return null;
    }
  },

  async isAuthenticated() {
    try {
      await getCurrentUser();
      return true;
    } catch (error) {
      return false;
    }
  }
};